import { NO_VALUE } from '../format.ts';
import { useCursor } from './cursor.ts';
import styles from './charts.module.css';

/* ==========================================================================
   The stamp — WHICH instant a panel is reading, said on the panel.

   A console bound to one cursor still owes the operator one thing per panel:
   the instant that panel's numbers came from. Without it two panels 130px
   apart can read `85.2 %` and `79.9 %` and both be right — one quoting the
   crosshair, one quoting the newest sample — and there is nothing on screen
   that says so. That is not a rounding disagreement, it is two clocks, and
   it is the failure this component exists to make impossible.

   Two parts, always in this order:

     WORD   where the instant came from — `cursor` while the pointer is on a
            plot, `pinned` for a playhead the operator left somewhere,
            `newest` when nothing is pointing and the panel has fallen back
            to the freshest sample it holds.
     TIME   the instant itself, mono and tabular, in accent whenever a
            crosshair rather than the newest sample is what is being quoted.
            Accent because that is the console's colour for "you moved this",
            and because a panel in replay must not look like a panel that is
            live.

   Every panel bound to `CursorCtx` should carry one, INCLUDING panels the kit
   does not draw: a vitals strip above a stack is a value panel like any
   other, and it either follows the crosshair and stamps it or it is the
   second clock. Mount it in a panel's `meta` and hand it the instant that
   panel is actually quoting — `cursor.at ?? <its own newest>` — so a panel
   whose newest sample is older than the window's edge stamps its sample's
   time and not the edge's.
   ========================================================================== */

export function CursorStamp({
  at,
  format,
  missing = NO_VALUE,
}: {
  /**
   * The instant this panel's readouts are quoting. Pass `cursor.at` when a
   * crosshair is up and the panel's own newest sample when it is not; pass
   * null when the panel has nothing to quote at all.
   */
  at: number | null;
  /** How this panel prints an instant — `13:58:38Z`, `08-30 13:58:38Z`, … */
  format: (t: number) => string;
  /** What stands in for the time when `at` is null. */
  missing?: string;
}) {
  const cursor = useCursor();
  const live = cursor.at !== null;
  return (
    <>
      <span className={styles.stampWord}>
        {live ? (cursor.hover !== null ? 'cursor' : 'pinned') : 'newest'}
      </span>
      <span className={styles.stampTime} data-live={live}>
        {at === null ? missing : format(at)}
      </span>
    </>
  );
}
